import {
  MakeSubRouteRef,
  PathParams,
  RouteRef,
  SubRouteRef,
  createRouteRef,
  createSubRouteRef,
} from '@backstage/core-plugin-api';

import { PageId } from '@spotify/plugin-confidence-tracking-react';

import { PageRouteRef, registerPage } from './pageRegistry';

type PageOptions = {
  title: string;
  defaultTab?: string;
};

type AbsolutePageConfig = {
  id: PageId;
  route?: never;
  page: PageOptions;
};

type SubPageConfig<Path extends string> = {
  id: PageId;
  route: { path: Path; parent: PageRouteRef };
  page: PageOptions;
};

const findAbsoluteRoot = (ref: PageRouteRef): RouteRef => {
  if (ref.$$type === '@backstage/RouteRef') {
    return ref;
  }
  return (ref as SubRouteRef).parent;
};

export function createPageRoute<Path extends string>(
  config: SubPageConfig<Path>,
): MakeSubRouteRef<PathParams<Path>, undefined>;

export function createPageRoute(
  config: AbsolutePageConfig,
): RouteRef<undefined>;

export function createPageRoute(
  config: AbsolutePageConfig | SubPageConfig<string>,
): PageRouteRef {
  const { id, route, page } = config;

  // Root-level page: standalone route ref
  if (!route) {
    const routeRef = createRouteRef({ id });
    registerPage({
      routeRef,
      title: page.title,
      defaultTab: page.defaultTab,
      id,
    });

    return routeRef;
  }

  // Nested page: sub-route
  const urlParent = findAbsoluteRoot(route.parent);
  const routeRef = createSubRouteRef({
    id,
    path: route.path,
    parent: urlParent,
  });

  registerPage({
    routeRef,
    parent: route.parent,
    title: page.title,
    defaultTab: page.defaultTab,
    id,
  });

  return routeRef;
}
